import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  CircularProgress,
  TextField,
  Button,
  Card,
  CardContent,
  Box,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { db, auth } from "../firebase-config";
import { doc, getDoc, setDoc, updateDoc, collection, addDoc } from "firebase/firestore";
import { Link } from "react-router-dom";

const Settings = () => {
  const [user, setUser] = useState(null);
  const [companyId, setCompanyId] = useState("");
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [newCompanyDomain, setNewCompanyDomain] = useState("");
  const [openCompanyModal, setOpenCompanyModal] = useState(false);

  const currentUser = auth.currentUser;

  useEffect(() => {
    if (!currentUser) return;

    const fetchUserData = async () => {
      try {
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          setUser(userData);
          setCompanyId(userData.companyId || "");

          if (userData.companyId) {
            // Fetch company details
            const companyRef = doc(db, "companies", userData.companyId);
            const companySnap = await getDoc(companyRef);
            if (companySnap.exists()) {
              setCompany({ id: companySnap.id, ...companySnap.data() });
            }
          }
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
        setError("Failed to load user data.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser]);

  const handleJoinCompany = async () => {
    if (!companyId) {
      setError("Company ID cannot be empty.");
      return;
    }

    try {
      const companyRef = doc(db, "companies", companyId);
      const companySnap = await getDoc(companyRef);

      if (!companySnap.exists()) {
        setError("Company not found.");
        return;
      }

      // Update user document with companyId
      const userRef = doc(db, "users", currentUser.uid);
      await updateDoc(userRef, { companyId });

      setCompany({ id: companyId, ...companySnap.data() });
      setError(null);
      alert("Successfully linked to company!");
    } catch (err) {
      console.error("Error linking company:", err);
      setError("Failed to link company.");
    }
  };

  const handleCreateCompany = async () => {
    if (!newCompanyName.trim()) {
      setError("Company name cannot be empty.");
      return;
    }
    if (!newCompanyDomain.trim()) {
      setError("Company domain cannot be empty.");
      return;
    }

    try {
      const companyRef = collection(db, "companies");
      const newCompanyRef = await addDoc(companyRef, {
        name: newCompanyName.trim(),
        domain: newCompanyDomain.trim(),
        approved: false, // Requires admin approval
        createdBy: currentUser.uid,
      });

      const userRef = doc(db, "users", currentUser.uid);
      await updateDoc(userRef, { companyId: newCompanyRef.id });

      setCompany({ id: newCompanyRef.id, name: newCompanyName, domain: newCompanyDomain, approved: false });
      setCompanyId(newCompanyRef.id);
      setNewCompanyName("");
      setNewCompanyDomain("");
      setOpenCompanyModal(false);
      alert("Company created! Awaiting admin approval.");
    } catch (err) {
      console.error("Error creating company:", err);
      setError("Failed to create company.");
    }
  };

  const handleUpdateCompanySettings = async () => {
    if (!company) return;

    try {
      const companyRef = doc(db, "companies", company.id);
      await updateDoc(companyRef, { name: company.name, domain: company.domain });

      alert("Company settings updated!");
    } catch (err) {
      console.error("Error updating company settings:", err);
      setError("Failed to update company settings.");
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6">User Information</Typography>
          <Typography variant="body1">Email: {currentUser.email}</Typography>

          <Button variant="contained" color="primary"  component={Link} to={"/logout"}>
            logout
          </Button>

        </CardContent>
      </Card>

      {company ? (
        // If the user is linked to a company, allow editing company settings
        <Card>
          <CardContent>
            <Typography variant="h6">Company Settings</Typography>
            <Typography variant="body1">Company ID: {company.id}</Typography>
            <TextField
              label="Company Name"
              variant="outlined"
              fullWidth
              value={company.name}
              onChange={(e) => setCompany({ ...company, name: e.target.value })}
              sx={{ mt: 2 }}
            />
            <TextField
              label="Company Domain"
              variant="outlined"
              fullWidth
              value={company.domain}
              onChange={(e) => setCompany({ ...company, domain: e.target.value })}
              sx={{ mt: 2 }}
            />
            <Button variant="contained" color="primary" onClick={handleUpdateCompanySettings} sx={{ mt: 2 }}>
              Update Company Settings
            </Button>
          </CardContent>
        </Card>
      ) : (
        // If the user is not linked to a company, allow them to join or create one
        <Card>
          <CardContent>
            <Typography variant="h6">Join a Company</Typography>
            <TextField
              label="Company ID"
              variant="outlined"
              fullWidth
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              sx={{ mt: 2 }}
            />
            <Button variant="contained" color="primary" onClick={handleJoinCompany} sx={{ mt: 2 }}>
              Join Company
            </Button>

            <Divider sx={{ my: 3 }} />

            <Button variant="contained" color="secondary" onClick={() => setOpenCompanyModal(true)}>
              Create New Company
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Modal for Creating a New Company */}
      <Dialog open={openCompanyModal} onClose={() => setOpenCompanyModal(false)}>
        <DialogTitle>Create a New Company</DialogTitle>
        <DialogContent>
          <TextField
            label="Company Name"
            variant="outlined"
            fullWidth
            value={newCompanyName}
            onChange={(e) => setNewCompanyName(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            label="Company Domain"
            variant="outlined"
            fullWidth
            value={newCompanyDomain}
            onChange={(e) => setNewCompanyDomain(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCompanyModal(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleCreateCompany}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Settings;
